import type { GpAssignmentMapPoint, NgpSamplerOption, OrganicStatus } from '../../data-access/types/gp-sampling-assignment';
import type { Organization } from '@organisations/data-access';

export const WITHOUT_LABORATORY_FILTER_ID = -1;

export type AnalysisTypeFilter = 'carbon' | 'granulo' | 'density';

export const ANALYSIS_TYPE_FILTERS: AnalysisTypeFilter[] = ['carbon', 'granulo', 'density'];

export type AssignmentState =
  | 'unassigned'
  | 'assigned'
  | 'sms_active'
  | 'visible'
  | 'hidden'
  | 'confirmed';

const INDUSTRIAL_ORG_ROLE_NAMES = ['INDUSTRIAL', 'SAIPOL'] as const;

function isIndustrialOrganisation(organisation: Organization): boolean {
  const roleName = organisation.role?.name;
  if (!roleName) {
    return false;
  }

  return INDUSTRIAL_ORG_ROLE_NAMES.includes(
    roleName as (typeof INDUSTRIAL_ORG_ROLE_NAMES)[number],
  );
}

export interface AssignmentMapFilters {
  organizationIds: number[];
  osOrganizationIds: number[];
  samplerIds: number[];
  laboratoryIds: number[];
  includeWithoutLaboratory: boolean;
  organicStatuses: OrganicStatus[];
  assignmentStates: AssignmentState[];
  analysisTypes: AnalysisTypeFilter[];
}

export const EMPTY_ASSIGNMENT_MAP_FILTERS: AssignmentMapFilters = {
  organizationIds: [],
  osOrganizationIds: [],
  samplerIds: [],
  laboratoryIds: [],
  includeWithoutLaboratory: false,
  organicStatuses: [],
  assignmentStates: [],
  analysisTypes: [],
};

export function countActiveAssignmentFilters(filters: AssignmentMapFilters): number {
  let count = 0;
  if (filters.organizationIds.length) count += 1;
  if (filters.osOrganizationIds.length) count += 1;
  if (filters.samplerIds.length) count += 1;
  if (filters.laboratoryIds.length || filters.includeWithoutLaboratory) count += 1;
  if (filters.organicStatuses.length) count += 1;
  if (filters.assignmentStates.length) count += 1;
  if (filters.analysisTypes.length) count += 1;
  return count;
}

function pointAssignmentStates(point: GpAssignmentMapPoint): Set<AssignmentState> {
  const states = new Set<AssignmentState>();
  if (point.sms_active) {
    states.add('sms_active');
  }
  if (point.sampler) {
    states.add('assigned');
    if (point.visible_to_sampler) {
      states.add('visible');
    } else if (point.visible_to_sampler === false) {
      states.add('hidden');
    }
    if (point.sampler_confirmed) {
      states.add('confirmed');
    }
  }
  if (!point.sampler && !point.sms_active) {
    states.add('unassigned');
  }
  return states;
}

export function matchesAssignmentMapFilters(
  point: GpAssignmentMapPoint,
  filters: AssignmentMapFilters,
): boolean {
  if (filters.organicStatuses.length && !filters.organicStatuses.includes(point.organic_status)) {
    return false;
  }

  if (filters.samplerIds.length) {
    if (!point.sampler || !filters.samplerIds.includes(point.sampler.id)) {
      return false;
    }
  }

  if (filters.laboratoryIds.length || filters.includeWithoutLaboratory) {
    const laboratoryId = point.laboratory?.id ?? null;
    const matchesLaboratory = laboratoryId != null && filters.laboratoryIds.includes(laboratoryId);
    const matchesWithout = filters.includeWithoutLaboratory && laboratoryId == null;
    if (!matchesLaboratory && !matchesWithout) {
      return false;
    }
  }

  if (filters.organizationIds.length) {
    const orgId = point.sampler_organization?.id;
    if (orgId == null || !filters.organizationIds.includes(orgId)) {
      return false;
    }
  }

  if (filters.osOrganizationIds.length) {
    const osOrgId = point.os_organization?.id;
    if (osOrgId == null || !filters.osOrganizationIds.includes(osOrgId)) {
      return false;
    }
  }

  if (filters.assignmentStates.length) {
    const states = pointAssignmentStates(point);
    const matches = filters.assignmentStates.some((state) => states.has(state));
    if (!matches) {
      return false;
    }
  }

  if (
    filters.analysisTypes.length
    && !filters.analysisTypes.includes(point.analysis_type as AnalysisTypeFilter)
  ) {
    return false;
  }

  return true;
}

export function filterAssignmentPoints(
  points: GpAssignmentMapPoint[],
  filters: AssignmentMapFilters,
): GpAssignmentMapPoint[] {
  if (!countActiveAssignmentFilters(filters)) {
    return points;
  }

  const filtered: GpAssignmentMapPoint[] = [];
  for (let i = 0; i < points.length; i += 1) {
    const point = points[i];
    if (matchesAssignmentMapFilters(point, filters)) {
      filtered.push(point);
    }
  }
  return filtered;
}

export function assignmentFiltersToApiBody(
  filters: AssignmentMapFilters,
  referenceDate?: string,
): Record<string, unknown> {
  const body: Record<string, unknown> = {};

  if (filters.organizationIds.length) {
    body.organization_ids = filters.organizationIds;
  }
  if (filters.osOrganizationIds.length) {
    body.os_organization_ids = filters.osOrganizationIds;
  }
  if (filters.samplerIds.length) {
    body.sampler_ids = filters.samplerIds;
  }
  if (filters.laboratoryIds.length) {
    body.laboratory_ids = filters.laboratoryIds;
  }
  if (filters.includeWithoutLaboratory) {
    body.include_without_laboratory = true;
  }
  if (filters.organicStatuses.length) {
    body.organic_statuses = filters.organicStatuses;
  }
  if (filters.assignmentStates.length) {
    body.assignment_states = filters.assignmentStates;
  }
  if (filters.analysisTypes.length) {
    body.analysis_types = filters.analysisTypes;
  }
  if (referenceDate) {
    body.reference_date = referenceDate;
  }

  return body;
}

export interface AssignmentFilterOptions {
  organizations: { value: string; label: string }[];
  osOrganizations: { value: string; label: string }[];
  samplers: { value: string; label: string }[];
  laboratories: { value: string; label: string }[];
}

function mergeOrganizationOptions(
  orgMap: Map<number, string>,
): { value: string; label: string }[] {
  return Array.from(orgMap.entries())
    .map(([id, name]) => ({ value: String(id), label: name }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function buildFilterOptionsFromPoints(
  points: GpAssignmentMapPoint[],
  samplerOptions: { value: string; label: string }[],
  ngpSamplers?: NgpSamplerOption[],
  organisations?: Organization[],
  laboratoryOptions: { value: string; label: string }[] = [],
): AssignmentFilterOptions {
  const samplerOrgMap = new Map<number, string>();
  const osOrgMap = new Map<number, string>();

  for (let i = 0; i < points.length; i += 1) {
    const samplerOrg = points[i].sampler_organization;
    if (samplerOrg?.id != null && samplerOrg.name) {
      samplerOrgMap.set(samplerOrg.id, samplerOrg.name);
    }

    const osOrg = points[i].os_organization;
    if (osOrg?.id != null && osOrg.name) {
      osOrgMap.set(osOrg.id, osOrg.name);
    }
  }

  ngpSamplers?.forEach((sampler) => {
    if (sampler.organization?.id != null && sampler.organization.name) {
      samplerOrgMap.set(sampler.organization.id, sampler.organization.name);
    }
  });

  organisations?.forEach((organisation) => {
    if (!isIndustrialOrganisation(organisation)) {
      osOrgMap.set(organisation.id, organisation.name);
    }
  });

  return {
    organizations: mergeOrganizationOptions(samplerOrgMap),
    osOrganizations: mergeOrganizationOptions(osOrgMap),
    samplers: samplerOptions,
    laboratories: laboratoryOptions,
  };
}
